import React from 'react'
import { Provider } from 'react-redux'
import { Router, Route, IndexRoute, applyRouterMiddleware } from 'react-router'
import useScroll from 'react-router-scroll/lib/useScroll'
import { push } from 'react-router-redux'
import { UserAuthWrapper } from 'redux-auth-wrapper'
import { ORCHESTRA_SOCKET, ORCHESTRA_SOCKET_2, ORCHESTRA_TV_SOCKET, ORCHESTRA_TV_SOCKET_2 } from '../constants'

import App from './App'
import Home from './Home'
import Login from './Login'
import Album from './Album'
import Image from './Image'
import Hierarchy from './Hierarchy'
import Timeline from './Timeline'
import Users from './Users'
import Share from './Share'
import ExternalShares from './ExternalShares'
import Playlists from './Playlists'
import PlaylistEditor from './PlaylistEditor'
import ListEditor from './ListEditor'
import List from './List'
import Queues from './Queues'
import DynamicLinks from './DynamicLinks'
import Libraries from './Libraries'
import Channels from './Channels'
import PresentChannel from './PresentChannel'
import Settings from './Settings'
import UpdateKeywordVisibility from './UpdateKeywordVisibility'
import UpdateKeywordStructure from './UpdateKeywordStructure'
import UpdateKeywordWeighting from './UpdateKeywordWeighting'
import ScriptsInfo from './ScriptsInfo'
import UserSessions from './UserSessions'
import Orchestras from './Orchestras'
import JoinOrchestra from './JoinOrchestra'

import { userHasPerm, isSuperAdmin } from '../utils/auth'

const MATCH_ALBUM_PATH = /\/album\/[a-z0-9]+/i
const MATCH_ALBUM_EDIT_PATH = /\/album\/[a-z0-9]+\/edit/i

const routerMiddeware = useScroll((prevProps, props) => {
  if (!prevProps) return true
  if (
    MATCH_ALBUM_PATH.test(prevProps.location.pathname) &&
    MATCH_ALBUM_EDIT_PATH.test(props.location.pathname)
  ) {
    return false
  }
  return true
})

const RequireAuth = UserAuthWrapper({
  authSelector: state => state.auth,
  predicate: auth => auth.authenticated,
  redirectAction: push,
  failureRedirectPath: '/login'
})

function createAuthWrapper (fn) {
  return UserAuthWrapper({
    authSelector: state => state.auth,
    predicate: auth => auth.authenticated && (!fn || fn(auth.user)),
    redirectAction: push,
    failureRedirectPath: '/',
    allowRedirectBack: false
  })
}

const RequireAdmin = createAuthWrapper(user => userHasPerm(user, 'admin'))
const RequireSuperAdmin = createAuthWrapper(user => isSuperAdmin(user))
const RequirePlaylistPerm = createAuthWrapper(user => userHasPerm(user, 'create_channels'))
const RequireExternalShare = createAuthWrapper(user => userHasPerm(user, 'external_share'))
const RequireChannels = createAuthWrapper(user => userHasPerm(user, 'create_channels') || userHasPerm(user, 'view_shared_channels'))
const RequireSearchPerm = createAuthWrapper(user => userHasPerm(user, 'search_and_save'))
const RequireDynamicLinks = createAuthWrapper(user => userHasPerm(user, 'dynamic_links'))
const RequireHierarchy = createAuthWrapper(user => userHasPerm(user, 'view_hierarchy'))
const RequireSettings = createAuthWrapper(user => userHasPerm(user, 'image_config') || isSuperAdmin(user))

export default function Root ({ store, history }) {
  return (
    <Provider store={store}>
      <Router history={history} render={applyRouterMiddleware(routerMiddeware)}>
        <Route path='/' component={App}>
          <IndexRoute component={RequireAuth(Home)} title='' showFeaturedAlbum />
          <Route path='login' component={Login} title='Login' />
          <Route path='search' component={RequireAuth(RequireSearchPerm(Album))} title='Search' />
          <Route path='timeline(/:decade)(/:year)(/:season)' component={RequireAuth(Timeline)} title='Timeline' />
          <Route path='hierarchy' component={RequireAuth(RequireHierarchy(Hierarchy))} title='Album Hierarchy' />
          <Route path='album(/:id)(/:task)' component={RequireAuth(Album)} />
          <Route path='albums' component={RequireAuth(Home)} title='Albums' />
          <Route path='image/:id' component={RequireAuth(Image)} title='Image' />
          <Route path='users' component={RequireAuth(RequireAdmin(Users))} title='Users' />
          <Route path='shares' component={RequireAuth(RequireExternalShare(ExternalShares))} title='Manage External Shares' />
          <Route path='share/:code' component={Share} title='Login' />
          <Route path='playlists' component={RequireAuth(RequirePlaylistPerm(Playlists))} title='Manage Playlists' playlists />
          <Route path='playlists/create' component={RequireAuth(RequirePlaylistPerm(PlaylistEditor))} title='Create Playlist' />
          <Route path='playlists/:id' component={RequireAuth(RequirePlaylistPerm(PlaylistEditor))} title='Edit Playlist' />
          <Route path='groups' component={RequireAuth(Playlists)} title='Manage Album Groups' />
          <Route path='groups/create' component={RequireAuth(ListEditor)} title='Create New Group' />
          <Route path='groups/:id' component={RequireAuth(ListEditor)} title='Edit Album Group' />
          <Route path='group/:id' component={RequireAuth(List)} title='Album Group' />
          <Route path='queues' component={RequireAuth(RequirePlaylistPerm(Queues))} title='Manage Queues' />
          <Route path='queues/:id' component={RequireAuth(RequirePlaylistPerm(Queues))} title='Edit Queue' />
          <Route path='dynamics' component={RequireAuth(RequireDynamicLinks(DynamicLinks))} title='Manage Dynamic Tables' />
          <Route path='libraries' component={RequireAuth(RequireSuperAdmin(Libraries))} title='Manage Libraries' />
          <Route path='manage-keyword-visibility' component={RequireAuth(RequireSuperAdmin(UpdateKeywordVisibility))} title='Manage Keyword Visibility' />
          <Route path='manage-keyword-weighting' component={RequireAuth(RequireSuperAdmin(UpdateKeywordWeighting))} title='Manage Keyword Weighting' />
          <Route path='manage-keyword-updates' component={RequireAuth(RequireSuperAdmin(UpdateKeywordStructure))} title='Manage Keyword Updates' />
          <Route path='channels' component={RequireAuth(RequireChannels(Channels))} title='Manage Channels' />
          <Route path='orchestras' component={RequireAuth(RequireChannels(Orchestras))} title='Manage Orchestras' socket={ORCHESTRA_SOCKET} />
          <Route path='orchestras2' component={RequireAuth(RequireChannels(Orchestras))} title='Manage Orchestras 2' socket={ORCHESTRA_SOCKET_2} />
          <Route path='orchestra-tv' component={RequireAuth(RequireChannels(Orchestras))} title='Manage Orchestra TV' socket={ORCHESTRA_TV_SOCKET} />
          <Route path='orchestra-tv2' component={RequireAuth(RequireChannels(Orchestras))} title='Manage Orchestra TV 2' socket={ORCHESTRA_TV_SOCKET_2} />
          <Route path='orchestras/join/:code' component={JoinOrchestra} title='Redirecting...' />
          <Route path='present/:instance_id(/:task)' component={RequireAuth(PresentChannel)} />
          <Route path='settings' component={RequireAuth(RequireSettings(Settings))} title='Manage Image Configuration' />
          <Route path='scripts' component={RequireAuth(RequireSuperAdmin(ScriptsInfo))} title='Scripts Info' />
          <Route path='sessions' component={RequireAuth(UserSessions)} title='Manage Sessions' />
        </Route>
      </Router>
    </Provider>
  )
}



// WEBPACK FOOTER //
// ./src/pages/Root.js